import { useState } from 'react';
import {
  DEFAULT_EXTENSION_MINUTES,
  MAX_TIMER_MINUTES,
  MIN_TIMER_MINUTES,
  formatCountdown,
  parseTimerMinutes,
} from '@filmsloth/shared';
import { useT } from '../i18n/index.js';
import { useCountdown } from '../lib/useCountdown.js';
import { useSession } from '../session/useSession.js';

/**
 * The round's clock, on the swiping screen.
 *
 * Two shapes, because it is asking two different things. While it runs it is a
 * single line nobody has to react to. When it runs out it becomes the question
 * the whole mechanic exists for, put to the host: more time, or the shortlist?
 * Everybody else can see the same countdown — being told the vote is coming
 * beats being dropped into one.
 */
export function RoundTimer() {
  const t = useT();
  const { state, isHost, extendTimer, setTimer, startRunoff } = useSession();
  const [extra, setExtra] = useState(String(DEFAULT_EXTENSION_MINUTES));

  const timer = state?.timer ?? null;
  const remaining = useCountdown(timer ? timer.remainingMs : null);
  const grace = useCountdown(timer?.expired ? timer.graceRemainingMs : null);

  if (!state || !timer) return null;

  // The local countdown decides too: the server notices the expiry within a
  // second, and a clock that reads 0:00 under a heading saying how much time
  // is left is worse than being a moment early.
  const expired = timer.expired || remaining <= 0;

  if (!expired) {
    return (
      <p className="round-timer">
        <span className="round-timer__clock">{formatCountdown(remaining)}</span>
        <span className="muted small">{t('timer.untilRunoff')}</span>
      </p>
    );
  }

  const host = state.participants.find((participant) => participant.id === state.hostId);
  const addMinutes = () => void extendTimer(parseTimerMinutes(extra));

  return (
    <div className="panel stack stack--tight round-timer--up">
      <span className="field__label">{t('timer.upTitle')}</span>
      {/* Only once the server agrees the time is up. Our own countdown gets
          there up to a second earlier, and in that gap `canStartRunoff` is
          still false — which would flash "too little is voted on" at a group
          about to be handed a perfectly good ballot. */}
      {timer.expired ? (
        <p className="muted small">
          {state.canStartRunoff
            ? t('timer.upBody', { time: formatCountdown(grace) })
            : t('timer.upBodyNothing')}
        </p>
      ) : null}

      {isHost ? (
        <>
          <div className="row timer-setting__row">
            <input
              className="input timer-setting__minutes"
              type="number"
              inputMode="numeric"
              min={MIN_TIMER_MINUTES}
              max={MAX_TIMER_MINUTES}
              value={extra}
              aria-label={t('timer.addLabel')}
              onChange={(event) => setExtra(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addMinutes();
              }}
            />
            <button type="button" className="btn btn--block" onClick={addMinutes}>
              {t('timer.add', { count: parseTimerMinutes(extra) })}
            </button>
          </div>

          {state.canStartRunoff ? (
            <button
              type="button"
              className="btn btn--primary btn--block"
              onClick={() => void startRunoff()}
            >
              {t('runoff.start')}
            </button>
          ) : null}

          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => void setTimer(null)}
          >
            {t('timer.off')}
          </button>
        </>
      ) : (
        <p className="muted small">{t('timer.upWaiting', { name: host?.name ?? '' })}</p>
      )}
    </div>
  );
}
